/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { IPaperSize, paperSizes, PaperTypes, PaperUnits } from './paper-sizes';
import { ICapturedTile, ITilePosition } from '@appCanvasCommon/export/ExportCapture';
/**
 * Available page orientations for print configuration.
 * Determines how the page dimensions are applied (width/height swapped for portrait).
 */
export enum PaperOrientations {
	Landscape = 'landscape',
	Portrait = 'portrait',
}
/**
 * Available print scope options that applies automatic scaling/fit to fill behaviours
 */
export enum PrintScopes {
	FitAll = 'fitAll',
	FitIncomplete = 'fitIncomplete',
	FitWithinDate = 'fitDateRange',
	FitPull = 'fitPull',
	Custom = 'custom',
}
/**
 * Text size options for rendered text elements in the print output. Acts as an override of `Ticket Font Size`
 */
export enum PrintTextSizes {
	Small = 'small',
	Medium = 'medium',
	Large = 'large',
}

/**
 * The action associated with the current configuration data. When set to `CaptureSingle` or `CaptureStitch`, it will
 * kickoff the plan capture and/or stitch sequence
 */
export enum PrintActions {
	Edit = 'edit',
	CaptureSingle = 'captureSingle',
	CaptureStitch = 'captureStitch',
	Cancel = 'cancel',
	PrintCompleted = 'printCompleted',
	PrintFailed = 'printFailed',
}

/**
 * Options for the role legend
 */
export enum RoleLegendOptions {
	None = 'none',
	TopLeft = 'topLeft',
	TopRight = 'topRight',
	BottomLeft = 'bottomLeft',
	BottomRight = 'bottomRight',
}

/**
 * Complete configuration interface for plan print functionality.
 */
export interface IPrintConfiguration {
	pageSize: PaperTypes;
	width: number;
	height: number;
	units: PaperUnits;
	orientation: PaperOrientations;
	dpi: number;
	startDate?: string;
	endDate?: string;
	textSize: PrintTextSizes;
	roleLegend: RoleLegendOptions;
	includeProjectLogo: boolean;
	scope: PrintScopes;
	action: PrintActions;
}
/**
 * Default paper size used when initializing print configuration
 */
const defaultPage: IPaperSize = paperSizes.get('letter') as IPaperSize;
/**
 * Default print configuration with sane defaults for most use cases.
 */
export const defaultPrintConfiguration: IPrintConfiguration = {
	pageSize: PaperTypes.Letter,
	width: defaultPage.width,
	height: defaultPage.height,
	units: defaultPage.units,
	orientation: PaperOrientations.Landscape,
	textSize: PrintTextSizes.Medium,
	roleLegend: RoleLegendOptions.BottomLeft,
	includeProjectLogo: true,
	dpi: 300,
	scope: PrintScopes.FitAll,
	action: PrintActions.Edit,
};

/**
 * Represents the current print status, including configuration state and lifecycle stage.
 * Used to broadcast updates during the print process.
 */
export interface IPrintStatus {
	config?: IPrintConfiguration;
	status: PrintActions;
	message?: string;
	isError?: boolean;
}

/**
 * Internal interface used to track images and their position during the print capture/stitch process.
 */
export interface IPlanPrintImage {
	blob: Blob;
	position: ITilePosition;
	index: number;
}
